import { zodResolver } from '@hookform/resolvers/zod';
import { DeploymentModel, DeploymentStatus, DeploymentType } from '@sunstone/api-client';
import {
  useCreateDeployment,
  useGetAdHocPreflightStatus,
  useGetBootstrapTemplate,
  useGetDeployment,
  useGetTenant,
  useListAuditEvents,
  useListTenants,
  useRunAdHocPreflight,
  useStreamDeploymentEvents,
} from '@sunstone/api-client/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  cn,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Stepper,
} from '@sunstone/ui';
import { Check, ChevronRight, Copy, ExternalLink, Loader2, ShieldCheck, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { z } from 'zod';
import { AuditHistory } from '@/components/ui/AuditHistory';
import { PageHeader } from '@/components/ui/PageHeader';
import { PreflightChecklistDialog } from '@/components/ui/PreflightChecklistDialog';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { useAuthorization, useAuthToken } from '@/features/auth';
import { useDebounce } from '@/hooks/useDebounce';
import { useSlugSync } from '@/hooks/useSlugSync';
import { flags } from '@/lib/flags';
import { REGIONS_BY_PROVIDER } from '@/lib/regions';
import { routes } from '@/lib/routes';
import { displayNameSchema, slugSchema } from '@/lib/schemas';

// ─── Provider → DeploymentType mapping ───────────────────────────────────────

const PROVIDER_TYPE: Record<string, DeploymentType> = {
  aws: DeploymentType.CLOUD_AWS,
  azure: DeploymentType.CLOUD_AZURE,
  gcp: DeploymentType.CLOUD_GCP,
};

const MODEL_ENUM: Record<string, DeploymentModel> = {
  HYBRID: DeploymentModel.HYBRID,
  SAAS: DeploymentModel.SAAS,
  AIRGAPPED: DeploymentModel.AIRGAPPED,
};

const MODEL_OPTIONS = [
  { value: 'HYBRID', label: 'Hybrid', description: 'Uniphore-Managed MP, Customer Infrastructure' },
  { value: 'SAAS', label: 'SaaS', description: 'Uniphore-Managed MP, Uniphore Infrastructure' },
  {
    value: 'AIRGAPPED',
    label: 'Airgapped',
    description: 'Customer-Managed MP, Customer Infrastructure',
  },
];

// ─── Static data ──────────────────────────────────────────────────────────────

const PROVIDERS = [
  {
    id: 'aws',
    label: 'Amazon Web Services',
    abbr: 'AWS',
    regions: REGIONS_BY_PROVIDER.aws,
    cfnLabel: 'Launch Stack in AWS Console',
  },
  {
    id: 'azure',
    label: 'Microsoft Azure',
    abbr: 'Azure',
    regions: REGIONS_BY_PROVIDER.azure,
    cfnLabel: 'Deploy Bicep Template',
  },
  {
    id: 'gcp',
    label: 'Google Cloud',
    abbr: 'GCP',
    regions: REGIONS_BY_PROVIDER.gcp,
    cfnLabel: 'Deploy Terraform Template',
  },
  {
    // On-prem clusters already exist and are managed by Duplo, so this card is an
    // entry point to the import flow rather than a provider you create into. It
    // carries no regions and never reaches PROVIDER_TYPE.
    id: 'onprem',
    label: 'Import a cluster',
    abbr: 'On-Prem',
    regions: [],
    cfnLabel: '',
  },
];

// ─── Deployment status groups ────────────────────────────────────────────────

const BOOTSTRAP_DONE_STATUSES = new Set<DeploymentStatus>([
  DeploymentStatus.PROVISIONING,
  DeploymentStatus.PHASE_1_COMPLETE,
  DeploymentStatus.PROVISIONED,
  DeploymentStatus.ACTIVE,
  DeploymentStatus.MAINTENANCE,
]);

const BOOTSTRAP_FAILED_STATUSES = new Set<DeploymentStatus>([
  DeploymentStatus.BOOTSTRAP_FAILED,
  DeploymentStatus.BOOTSTRAP_TIMEOUT,
]);

const AWAITING_BOOTSTRAP_STATUSES = new Set<DeploymentStatus>([
  DeploymentStatus.CREATED,
  DeploymentStatus.AWAITING_BOOTSTRAP,
]);

// ─── Wizard steps ─────────────────────────────────────────────────────────────

const WIZARD_STEPS = ['Name & Provider', 'Region', 'Deploy', 'Provisioning'];

// ─── Validation schema ────────────────────────────────────────────────────────

const schema = z.object({
  tenantId: z.string().min(1, 'Select a customer'),
  deploymentName: displayNameSchema,
  slug: slugSchema,
  providerId: z.string().min(1, 'Select a cloud provider'),
  deploymentModel: z.enum(['HYBRID', 'SAAS', 'AIRGAPPED']),
  region: z.string().min(1, 'Select a region'),
});

const step1Schema = schema.pick({
  tenantId: true,
  deploymentName: true,
  slug: true,
  providerId: true,
  deploymentModel: true,
});

type FormValues = z.infer<typeof schema>;

// ─── Main wizard ─────────────────────────────────────────────────────────────

export function NewDeploymentPage() {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { token } = useAuthToken();
  const { scope, tenantId: authTenantId, hasPermission } = useAuthorization();

  // Tenant-scoped users can only create deployments within their own tenant.
  // If no tenantId URL param is present, fall back to the user's own tenantId.
  const isTenantScoped = scope === 'tenant';
  const resolvedTenantParam = tenantId || (isTenantScoped ? authTenantId : undefined);

  const [tenantSearch, setTenantSearch] = useState('');
  const debouncedTenantSearch = useDebounce(tenantSearch);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlDeploymentId = searchParams.get('deploymentId');

  const [step, setStep] = useState(1);
  const [createdDeploymentId, setCreatedDeploymentId] = useState<string | undefined>(
    urlDeploymentId ?? undefined,
  );
  const [showTemplate, setShowTemplate] = useState(false);
  const [copied, setCopied] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      tenantId: resolvedTenantParam ?? '',
      deploymentName: '',
      slug: '',
      providerId: '',
      deploymentModel: 'HYBRID',
      region: '',
    },
  });

  const tenantIdValue = form.watch('tenantId');
  const deploymentNameValue = form.watch('deploymentName');
  const slugValue = form.watch('slug');
  const providerIdValue = form.watch('providerId');
  const deploymentModelValue = form.watch('deploymentModel');
  const provider = PROVIDERS.find((p) => p.id === providerIdValue);

  const resolvedTenantId = tenantIdValue;

  const { data: tenantData } = useGetTenant(resolvedTenantId ?? '', token);
  const tenant = tenantData?.tenant;

  const { data: tenantsData } = useListTenants(
    token,
    undefined,
    { search: debouncedTenantSearch, pageSize: 20 },
    !resolvedTenantParam,
  );
  const tenants = tenantsData?.tenants ?? [];
  const createDeployment = useCreateDeployment(token);

  const step1Valid = step1Schema.safeParse({
    tenantId: tenantIdValue,
    deploymentName: deploymentNameValue,
    slug: slugValue,
    providerId: providerIdValue,
    deploymentModel: deploymentModelValue,
  }).success;

  const syncSlug = useSlugSync(form);

  // Pre-create preflight (gated by VITE_SHOW_PREFLIGHT_BUTTON). Runs the
  // pre-deploy-deployment readiness checks against a caller-supplied role in the
  // target account, BEFORE the deployment is created. The region comes from the
  // wizard; the operator supplies a role they can already assume (Sunstone's own
  // cross-account role doesn't exist until bootstrap).
  const regionValue = form.watch('region');
  const runAdHocPreflight = useRunAdHocPreflight(token);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflightStarted, setPreflightStarted] = useState(false);
  const [preflightWorkflowId, setPreflightWorkflowId] = useState('');
  const [preflightAccountId, setPreflightAccountId] = useState('');
  // gateMode: the preflight run is gating deployment creation (opened from the
  // Create button) rather than the standalone read-only "Run pre-create checks"
  // preview. Drives the dialog's previewOnly + whether proceeding creates.
  const [gateMode, setGateMode] = useState(false);
  // Immediate verdict (new account / role not assumable) shown inline when the
  // server returns a message instead of starting a workflow.
  const [preflightVerdict, setPreflightVerdict] = useState('');
  const preflightStatus = useGetAdHocPreflightStatus(
    preflightWorkflowId,
    token,
    preflightOpen && preflightStarted,
  );

  function runPreCreatePreflight() {
    const accountId = preflightAccountId.trim();
    if (!accountId || !regionValue) return;
    // Reset the poll-enabling state BEFORE the run so the status poll never fires
    // for a stale/again workflow id before this run is confirmed started.
    setGateMode(false);
    setPreflightStarted(false);
    setPreflightWorkflowId('');
    setPreflightVerdict('');
    runAdHocPreflight.mutate(
      { region: regionValue, accountId, scope: 'pre-deploy-deployment' },
      {
        onSuccess: (res) => {
          if (res.workflowId) {
            // A run started: poll + show the checklist.
            setPreflightWorkflowId(res.workflowId);
            setPreflightStarted(true);
            setPreflightOpen(true);
          } else {
            // Immediate verdict (new account / role not assumable): show inline.
            setPreflightVerdict(res.message || 'Pre-create checks are not applicable.');
          }
        },
      },
    );
  }

  const isActiveDeployment =
    (step === 3 || step === 4 || !!urlDeploymentId) && !!createdDeploymentId;
  useStreamDeploymentEvents(isActiveDeployment ? (createdDeploymentId ?? '') : '', token);
  const { data: deploymentData, isLoading: isDeploymentLoading } = useGetDeployment(
    createdDeploymentId ?? '',
    token,
  );
  const { data: auditEventsData } = useListAuditEvents(
    isActiveDeployment ? token : null,
    'deployment',
    createdDeploymentId ?? '',
  );
  const deploymentStatus = deploymentData?.deployment?.status;

  // Extract bootstrap console URL from the GET API result.
  // Sanitize to prevent XSS via javascript: or data: protocol URLs.
  const rawBootstrapConsoleUrl = deploymentData?.bootstrapConsoleUrl ?? '';
  const bootstrapConsoleUrl = /^https?:\/\//i.test(rawBootstrapConsoleUrl)
    ? rawBootstrapConsoleUrl
    : '';

  // Fetch bootstrap template content via authenticated API (not direct S3 access)
  const { data: templateData, isLoading: templateLoading } = useGetBootstrapTemplate(
    createdDeploymentId ?? '',
    token,
    showTemplate,
  );
  const templateContent = templateData?.template ?? null;

  // Restore provider selection when resuming from URL
  useEffect(() => {
    if (!deploymentData?.deployment || form.getValues('providerId')) return;

    const providerType = deploymentData.deployment.deploymentType;
    const matchedProvider = Object.entries(PROVIDER_TYPE).find(
      ([_, type]) => type === providerType,
    )?.[0];

    if (matchedProvider) {
      form.setValue('providerId', matchedProvider, { shouldValidate: true });
    }
  }, [deploymentData?.deployment?.deploymentType, deploymentData?.deployment, form]);

  // Sync UI step with deployment status
  useEffect(() => {
    const status = deploymentData?.deployment?.status;
    if (status === undefined) return;

    if (BOOTSTRAP_DONE_STATUSES.has(status)) {
      setStep(4);
    } else if (AWAITING_BOOTSTRAP_STATUSES.has(status) || BOOTSTRAP_FAILED_STATUSES.has(status)) {
      setStep((prev) => (prev < 3 ? 3 : prev));
    }
  }, [deploymentData?.deployment?.status]);

  // Auto-navigate to deployment detail 2 seconds after going ACTIVE.
  useEffect(() => {
    const autoRedirectStatus =
      deploymentStatus === DeploymentStatus.ACTIVE ||
      deploymentStatus === DeploymentStatus.BOOTSTRAP_FAILED;
    if (step !== 4 || !autoRedirectStatus) return;
    if (!createdDeploymentId || !resolvedTenantId) return;
    const timer = setTimeout(() => {
      navigate(routes.deployment(resolvedTenantId, createdDeploymentId));
    }, 2000);
    return () => clearTimeout(timer);
  }, [step, deploymentStatus, createdDeploymentId, navigate, resolvedTenantId]);

  // Detect if deployment entered a failed/timeout state while on step 3.
  const bootstrapFailed =
    step === 3 && deploymentStatus !== undefined && BOOTSTRAP_FAILED_STATUSES.has(deploymentStatus);
  const isTimeout = deploymentStatus === DeploymentStatus.BOOTSTRAP_TIMEOUT;

  async function handleNextStep() {
    const valid = await form.trigger(['deploymentName', 'slug', 'providerId', 'deploymentModel']);
    if (valid) setStep(2);
  }

  // resetGateState clears the shared preflight/gate state so the next run (or the
  // manual preview) starts clean and the shared dialog never inherits stale gate
  // semantics or a prior report.
  function resetGateState() {
    setGateMode(false);
    setPreflightStarted(false);
    setPreflightWorkflowId('');
  }

  // doCreateDeployment performs the actual create (no gating). Invoked directly
  // when the gate is off/can't run, or from the checklist dialog once the operator
  // proceeds. On failure it closes the gate dialog so the inline error Alert (which
  // would otherwise be hidden behind the modal) is visible, and swallows the
  // rejection so the `void`-ed callers don't produce an unhandled rejection —
  // createDeployment.error carries the message for display.
  async function doCreateDeployment() {
    const values = form.getValues();
    let result: Awaited<ReturnType<typeof createDeployment.mutateAsync>>;
    try {
      result = await createDeployment.mutateAsync({
        tenantId: resolvedTenantId ?? '',
        displayName: values.deploymentName.trim(),
        slug: values.slug,
        deploymentType: PROVIDER_TYPE[values.providerId] ?? DeploymentType.UNSPECIFIED,
        deploymentModel: MODEL_ENUM[values.deploymentModel] ?? DeploymentModel.HYBRID,
        region: values.region,
      });
    } catch {
      // Close the dialog to reveal the createDeployment.error Alert on the form.
      setPreflightOpen(false);
      resetGateState();
      return;
    }
    setPreflightOpen(false);
    resetGateState();
    const newId = result.deployment?.id;
    setCreatedDeploymentId(newId);
    if (newId) setSearchParams({ deploymentId: newId });
    setStep(3);
  }

  // startGatePreflight runs the pre-deploy-deployment preflight for the create
  // gate and opens the checklist dialog. proceedOnUnavailable controls the
  // "can't verify" branch: the initial Create click proceeds with creation
  // (best-effort), but a dialog Re-run only re-checks and NEVER creates.
  function startGatePreflight(proceedOnUnavailable: boolean) {
    const accountId = preflightAccountId.trim();
    if (!accountId || !regionValue) {
      if (proceedOnUnavailable) void doCreateDeployment();
      return;
    }
    setGateMode(true);
    setPreflightStarted(false);
    setPreflightWorkflowId('');
    setPreflightVerdict('');
    runAdHocPreflight.mutate(
      { region: regionValue, accountId, scope: 'pre-deploy-deployment' },
      {
        onSuccess: (res) => {
          if (res.workflowId) {
            // A run started: poll + gate on the checklist.
            setPreflightWorkflowId(res.workflowId);
            setPreflightStarted(true);
            setPreflightOpen(true);
          } else if (proceedOnUnavailable) {
            // Initial create, can't verify (new account / role not assumable) → proceed.
            void doCreateDeployment();
          } else {
            // Re-run with nothing to poll: close the gate dialog (else it would spin
            // with no workflow) so the inline verdict below the account field shows.
            // Never create from a re-run.
            setPreflightOpen(false);
            resetGateState();
            setPreflightVerdict(res.message || 'Pre-create checks are not applicable.');
          }
        },
        onError: (err) => {
          // Starting preflight failed (transient/server error) — do NOT silently
          // create ungated. Close the gate dialog (if a re-run left it open) so the
          // message isn't hidden behind it, and surface it inline so the operator
          // can retry (or clear the account id to create without checks).
          setPreflightOpen(false);
          resetGateState();
          setPreflightVerdict(
            `Could not start pre-create checks: ${err.message}. Try again, or clear the account ID to create without checks.`,
          );
        },
      },
    );
  }

  // handleCreateClick is the Create button handler. When the deployment-create
  // gate is enabled it runs the pre-deploy-deployment preflight against the
  // supplied account and opens the checklist dialog; creation then happens on
  // proceed. Preflight can only run with a target account ID + an assumable role;
  // when it can't run (no account, or an immediate "not applicable" verdict) we
  // proceed with creation directly (best-effort gate).
  async function handleCreateClick() {
    if (!flags.runPreflightOnDeploymentCreate || !preflightAccountId.trim() || !regionValue) {
      await doCreateDeployment();
      return;
    }
    startGatePreflight(true);
  }

  // Attempting to restore from URL — suspend normal rendering until data loads
  if (urlDeploymentId && isDeploymentLoading && !deploymentData) {
    return (
      <div className="flex items-center justify-center p-24 text-muted-foreground">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
          <span>Loading deployment…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title="New Deployment"
        description={
          tenant
            ? `Setting up a new deployment for ${tenant.displayName}.`
            : 'Setting up a new deployment.'
        }
      />

      <Stepper steps={WIZARD_STEPS} current={step} className="mb-8" />

      <Form {...form}>
        <form>
          {/* Step 1: Name & Provider */}
          {step === 1 && (
            <Card>
              <CardContent className="py-6 space-y-6">
                <FormField
                  control={form.control}
                  name="tenantId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Customer <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        {resolvedTenantParam ? (
                          <Input value={tenant?.displayName ?? resolvedTenantParam} disabled />
                        ) : (
                          <SearchableCombobox
                            value={field.value}
                            onChange={(val) => {
                              field.onChange(val);
                              setTenantSearch('');
                            }}
                            items={tenants}
                            search={tenantSearch}
                            onSearchChange={setTenantSearch}
                            placeholder="Select a customer…"
                            searchPlaceholder="Search customers…"
                            emptyMessage="No customers found."
                            autoOpen
                            autoOpenDelay={600}
                          />
                        )}
                      </FormControl>
                      {!resolvedTenantParam && (
                        <FormDescription>Showing first 20 — type to search.</FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="deploymentName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Deployment name <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g. claims-prod-us"
                          autoFocus={!!resolvedTenantParam}
                          onChange={(e) => syncSlug(e.target.value, field.onChange)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        URL Name <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g. claims-prod-us" />
                      </FormControl>
                      <FormDescription>Used in URLs and API references.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="providerId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Cloud provider <span className="text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        {/* Card-style toggle: no @sunstone/ui ToggleGroup equivalent yet */}
                        <div className="grid grid-cols-4 gap-3">
                          {PROVIDERS.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => {
                                // On-prem clusters are imported, not created.
                                if (p.id === 'onprem') {
                                  navigate(routes.importOnPremCluster());
                                  return;
                                }
                                field.onChange(p.id);
                                form.setValue('region', '');
                              }}
                              className={cn(
                                'flex flex-col items-center gap-1 rounded-lg border px-4 py-5 text-center transition-all cursor-pointer',
                                field.value === p.id
                                  ? 'border-primary bg-primary/5 text-primary'
                                  : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground',
                              )}
                            >
                              <span className="text-sm font-bold">{p.abbr}</span>
                              <span className="text-xs">{p.label}</span>
                            </button>
                          ))}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="deploymentModel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Deployment model</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger id="dep-model">
                            <SelectValue placeholder="Select a deployment model…" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {MODEL_OPTIONS.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label} — {m.description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end">
                  <Button size="sm" type="button" onClick={handleNextStep} disabled={!step1Valid}>
                    Next: Select Region →
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Region */}
          {step === 2 && provider && (
            <Card>
              <CardContent className="py-6 space-y-5">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-foreground">{provider.label}</p>
                </div>

                {createDeployment.error && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      {createDeployment.error.message ??
                        'Failed to create deployment. Please try again.'}
                    </AlertDescription>
                  </Alert>
                )}

                <FormField
                  control={form.control}
                  name="region"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Region <span className="text-destructive">*</span>
                      </FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger id="dep-region">
                            <SelectValue placeholder="Select a region…" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {provider.regions.map((r) => (
                            <SelectItem key={r.value} value={r.value}>
                              {r.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {(flags.showPreflightButton || flags.runPreflightOnDeploymentCreate) &&
                  regionValue && (
                    <div className="space-y-3 rounded-md border bg-muted/30 p-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          Pre-create checks (optional)
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Run this only if a deployment was previously created or attempted on this
                          account. For a brand-new account, skip it. We look up the account's
                          previously-recorded role to verify it's clean before bootstrapping.
                        </p>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="preflight-account-id" className="text-xs">
                          Target AWS account ID
                        </Label>
                        <Input
                          id="preflight-account-id"
                          className="text-xs"
                          placeholder="123456789012"
                          value={preflightAccountId}
                          onChange={(e) => setPreflightAccountId(e.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={runPreCreatePreflight}
                        disabled={!preflightAccountId.trim() || runAdHocPreflight.isPending}
                      >
                        <ShieldCheck className="h-4 w-4" />
                        {runAdHocPreflight.isPending ? 'Starting checks…' : 'Run pre-create checks'}
                      </Button>
                      {preflightVerdict && (
                        <Alert>
                          <AlertDescription>{preflightVerdict}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}

                <div className="flex items-center justify-between pt-2">
                  <Button variant="ghost" size="sm" type="button" onClick={() => setStep(1)}>
                    ← Back
                  </Button>
                  <Button
                    size="sm"
                    type="button"
                    disabled={
                      !form.formState.isValid ||
                      createDeployment.isPending ||
                      runAdHocPreflight.isPending ||
                      !hasPermission('create:deployment')
                    }
                    onClick={handleCreateClick}
                  >
                    {createDeployment.isPending ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Creating…
                      </>
                    ) : (
                      'Generate CloudFormation Template →'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </form>
      </Form>

      {/* Step 3: CFN link + await callback */}
      {step === 3 && provider && (
        <div className="space-y-4">
          <Card>
            <CardContent className="py-6 space-y-4">
              {/* Status indicator */}
              <div className="flex items-center gap-2 text-xs">
                {bootstrapFailed ? (
                  <>
                    <div className="w-4 h-4 rounded-full bg-destructive/20 flex items-center justify-center shrink-0">
                      <X className="w-2.5 h-2.5 text-destructive" />
                    </div>
                    <span className="font-medium text-destructive">
                      {isTimeout ? 'Bootstrap timed out' : 'Bootstrap failed'}
                    </span>
                    <span className="text-muted-foreground">
                      —{' '}
                      {isTimeout
                        ? 'Token is still valid; if the stack completes later, provisioning starts automatically.'
                        : 'Check CloudFormation stack events for details.'}
                    </span>
                  </>
                ) : (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />
                    <span className="font-medium text-muted-foreground">
                      Awaiting stack registration…
                    </span>
                    <span className="text-muted-foreground/70">
                      This page updates automatically.
                    </span>
                  </>
                )}
              </div>

              <div>
                <p className="text-sm font-semibold text-foreground mb-1">
                  Your CloudFormation template is ready
                </p>
                <p className="text-xs text-muted-foreground">
                  Click the button below to open the AWS CloudFormation console with the template
                  pre-loaded. Run the stack in your AWS account — it will register this deployment
                  with Uniphore automatically.
                </p>
              </div>

              <div className="flex items-center gap-3">
                {bootstrapConsoleUrl && (
                  <Button asChild>
                    <a href={bootstrapConsoleUrl} target="_blank" rel="noopener noreferrer">
                      {provider.cfnLabel}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </Button>
                )}
              </div>

              {createdDeploymentId && (
                <>
                  <Separator />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowTemplate(!showTemplate)}
                    className="h-auto p-0 text-xs font-medium text-muted-foreground hover:text-foreground gap-1.5"
                  >
                    <ChevronRight
                      className={cn(
                        'w-3.5 h-3.5 transition-transform',
                        showTemplate && 'rotate-90',
                      )}
                    />
                    {showTemplate ? 'Hide template' : 'View template'}
                  </Button>
                </>
              )}

              {showTemplate && (
                <div className="relative rounded-md border border-border bg-muted/20 overflow-hidden group">
                  {templateLoading ? (
                    <div className="flex items-center gap-2 px-4 py-8 justify-center text-xs text-muted-foreground">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Loading template…
                    </div>
                  ) : (
                    <>
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (templateContent) {
                              navigator.clipboard.writeText(templateContent);
                              setCopied(true);
                              setTimeout(() => setCopied(false), 2000);
                            }
                          }}
                          className="h-7 w-7 bg-background/80 hover:bg-muted border border-border shadow-sm"
                          title="Copy template"
                        >
                          {copied ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <pre className="overflow-auto max-h-128 px-4 py-3 text-xs leading-relaxed font-mono text-foreground/90">
                        <code>{templateContent}</code>
                      </pre>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {bootstrapFailed && (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() =>
                  navigate(
                    createdDeploymentId && resolvedTenantId
                      ? routes.deployment(resolvedTenantId, createdDeploymentId)
                      : resolvedTenantId
                        ? routes.tenant(resolvedTenantId)
                        : routes.deployments(),
                  )
                }
              >
                View Deployment →
              </Button>
            </div>
          )}

          <ProvisioningEventFeed events={auditEventsData?.events ?? []} isActive={false} />
        </div>
      )}

      {/* Step 4: Provisioning status */}
      {step === 4 && (
        <div className="space-y-4">
          <Card>
            <CardContent className="py-6 space-y-4">
              <div className="flex items-center gap-3">
                {deploymentStatus === DeploymentStatus.ACTIVE ? (
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-green-500" />
                  </div>
                ) : (
                  <Loader2 className="w-5 h-5 animate-spin text-primary shrink-0" />
                )}
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {deploymentStatus === DeploymentStatus.ACTIVE
                      ? 'Deployment is active'
                      : 'Provisioning infrastructure…'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {deploymentStatus === DeploymentStatus.ACTIVE
                      ? 'Your deployment is fully operational. You can now create workspaces.'
                      : 'Bootstrap callback received. The Management Plane is now provisioning EKS clusters, networking, and platform components. This may take several minutes.'}
                  </p>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  size="sm"
                  onClick={() =>
                    navigate(
                      createdDeploymentId && resolvedTenantId
                        ? routes.deployment(resolvedTenantId, createdDeploymentId)
                        : resolvedTenantId
                          ? routes.tenant(resolvedTenantId)
                          : routes.deployments(),
                    )
                  }
                >
                  View Deployment →
                </Button>
              </div>
            </CardContent>
          </Card>

          <ProvisioningEventFeed
            events={auditEventsData?.events ?? []}
            isActive={deploymentStatus === DeploymentStatus.ACTIVE}
          />
        </div>
      )}

      {(flags.showPreflightButton || flags.runPreflightOnDeploymentCreate) && (
        <PreflightChecklistDialog
          open={preflightOpen}
          onOpenChange={(open) => {
            setPreflightOpen(open);
            // Reset shared gate state when the operator dismisses the dialog so a
            // later run / the manual preview never inherits stale gate semantics.
            if (!open) resetGateState();
          }}
          status={preflightStatus.data}
          isLoading={
            runAdHocPreflight.isPending ||
            createDeployment.isPending ||
            (preflightStarted && !preflightStatus.data)
          }
          onRerun={() => (gateMode ? startGatePreflight(false) : runPreCreatePreflight())}
          onProceed={() => void doCreateDeployment()}
          previewOnly={!gateMode}
        />
      )}
    </div>
  );
}

// ─── Provisioning event feed ──────────────────────────────────────────────────

interface ProvisioningEventFeedProps {
  events: Parameters<typeof AuditHistory>[0]['events'];
  isActive: boolean;
}

function ProvisioningEventFeed({ events, isActive }: ProvisioningEventFeedProps) {
  const recent = events.slice(0, 10);

  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!isActive && (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground shrink-0" />
            )}
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Provisioning activity
            </p>
          </div>
        </div>

        {recent.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Waiting for activity…</p>
        ) : (
          <AuditHistory events={recent} />
        )}
      </CardContent>
    </Card>
  );
}
